import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/client";
import {
  useTenantScope,
  normalizeScopePlatform,
} from "../scope/TenantScopeContext";

const panel = {
  background: "#101923",
  border: "1px solid #263747",
  borderRadius: 14,
  padding: 20,
  marginBottom: 18,
};

const cell = {
  padding: 9,
  borderBottom: "1px solid #263747",
  verticalAlign: "top",
};

function profile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || "null");
      if (value && typeof value === "object") return value;
    } catch (_) {}
  }
  return {};
}

async function api(path) {
  const response = await apiFetch(path);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || `HTTP ${response.status}`);
  }
  return body;
}

export default function AssetTopologyPage() {
  const scope = useTenantScope();
  const user = useMemo(profile, []);
  const role = user.role || localStorage.getItem("role") || "";

  const companyId =
    role === "super_admin"
      ? String(scope.companyId || "")
      : String(user.company_id || localStorage.getItem("company_id") || "");

  const [devices, setDevices] = useState([]);
  const [runtime, setRuntime] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (role === "super_admin" && !companyId) {
      setDevices([]);
      setRuntime([]);
      return;
    }

    try {
      setError("");

      const suffix =
        role === "super_admin"
          ? `?company_id=${encodeURIComponent(companyId)}`
          : "";

      const [inventoryRows, runtimeRows] = await Promise.all([
        api(`/endpoint-inventory/devices${suffix}`),
        api(`/runtime-telemetry/devices${suffix}`),
      ]);

      setDevices(Array.isArray(inventoryRows) ? inventoryRows : []);
      setRuntime(Array.isArray(runtimeRows) ? runtimeRows : []);
    } catch (err) {
      setError(err.message);
    }
  }, [companyId, role]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleDevices = useMemo(
    () =>
      devices.filter(
        (item) =>
          (!scope.deviceUuid ||
            String(item.device_uuid) === String(scope.deviceUuid)) &&
          (!scope.platform ||
            scope.platform === "all" ||
            normalizeScopePlatform(item.platform || item.os?.name) ===
              scope.platform)
      ),
    [devices, scope.deviceUuid, scope.platform]
  );

  const runtimeByDevice = useMemo(
    () =>
      new Map(
        runtime.map((item) => [
          String(item.device_uuid),
          item,
        ])
      ),
    [runtime]
  );

  return (
    <main
      style={{
        color: "#eef5fb",
        padding: 24,
        maxWidth: 1500,
        margin: "0 auto",
      }}
    >
      <h1>Asset Topology</h1>
      <p style={{ color: "#a9bbca" }}>
        Live topology derived from the canonical endpoint inventory and runtime
        telemetry pipelines. No legacy CMDB projection is used by this view.
      </p>

      {role === "super_admin" && (
        <section style={panel}>
          <strong>Tenant scope</strong>
          <div style={{ color: "#a9bbca", marginTop: 6 }}>
            {companyId
              ? `Company ${companyId}`
              : "Select a company from the global scope control."}
          </div>
        </section>
      )}

      {error && (
        <section
          style={{
            ...panel,
            borderColor: "#b42318",
            color: "#ffb4ab",
          }}
        >
          {error}
        </section>
      )}

      <section style={panel}>
        <h2>Managed endpoint nodes</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
            gap: 12,
          }}
        >
          {visibleDevices.map((device) => {
            const rt = runtimeByDevice.get(String(device.device_uuid));

            return (
              <Link
                key={device.device_uuid}
                to={`/assets/${encodeURIComponent(device.device_uuid)}`}
                style={{
                  display: "block",
                  color: "#eef5fb",
                  textDecoration: "none",
                  border: "1px solid #31485b",
                  borderRadius: 10,
                  padding: 14,
                  background: "#0a1118",
                }}
              >
                <strong>
                  {device.device_name ||
                    device.hostname ||
                    device.device_uuid}
                </strong>

                <div style={{ color: "#a9bbca", marginTop: 6 }}>
                  {device.os?.name || device.platform || "Unknown platform"}
                </div>

                <div style={{ marginTop: 8 }}>
                  Applications: {device.installed_application_count || 0}
                </div>

                <div>
                  Processes: {rt?.process_count ?? "Not reported"}
                </div>

                <div>
                  Services: {rt?.service_count ?? "Not reported"}
                </div>

                <div>
                  Connections: {rt?.network_connection_count ?? "Not reported"}
                </div>
              </Link>
            );
          })}
        </div>

        {!visibleDevices.length && (
          <p>No modern endpoint inventory is visible for the selected scope.</p>
        )}
      </section>

      <section style={panel}>
        <h2>Observed network relationships</h2>

        <div style={{ overflowX: "auto", maxHeight: 520 }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                {["Device", "Platform", "Connections", "Observed"].map(
                  (name) => (
                    <th key={name} style={{ ...cell, textAlign: "left" }}>
                      {name}
                    </th>
                  )
                )}
              </tr>
            </thead>

            <tbody>
              {visibleDevices.map((device) => {
                const rt = runtimeByDevice.get(String(device.device_uuid));

                return (
                  <tr key={device.device_uuid}>
                    <td style={cell}>
                      <Link
                        to={`/assets/${encodeURIComponent(device.device_uuid)}`}
                        style={{ color: "#9fd3ff" }}
                      >
                        {device.device_name ||
                          device.hostname ||
                          device.device_uuid}
                      </Link>
                    </td>

                    <td style={cell}>
                      {device.os?.name || device.platform || "Unknown"}
                    </td>

                    <td style={cell}>
                      {rt?.network_connection_count ?? "Not reported"}
                    </td>

                    <td style={cell}>
                      {rt?.observed_at || "Not reported"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
